import { useState } from 'react';
import { API } from '../config/config';
import { useAuth } from './useAuth';

// Define the AdminBuyerRequest interface
export interface AdminBuyerRequest {
  email: string;
  name: string;
}

interface CreateOrderRequest {
  variantId: string;
  addressId: string;
  amountPaid: number;
  quantity: number;
  notes?: string;
}

interface AdminCreateOrderRequest {
  email: string;
  discountCodes?: string[];
  order: CreateOrderRequest[];
}

export const useOrderMgt = () => {
  const { enhancedFetch } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Function to verify a user
  const verifyUser = async (userData: AdminBuyerRequest) => {
    setLoading(true);
    setError(null);
    setSuccess(false);
    
    try {
      // Use enhancedFetch to make the POST request
      const response = await enhancedFetch(`${API.HOST}/api/v1/users/buyers/unverified`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(userData)
      });
      
      setSuccess(true);
      return response;
    } catch (err) {
      setError(err.message);
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Submit an order request
  const submitOrderRequest = async (orderData: AdminCreateOrderRequest) => {
    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      const response = await enhancedFetch(`${API.HOST}/api/v1/orders`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: orderData.email,
          discountCodes: orderData.discountCodes || [''],
          orders: orderData.order.map((order) => ({
            variantId: order.variantId,
            addressId: order.addressId,
            amountPaid: order.amountPaid,
            quantity: order.quantity,
            notes: order.notes || ''
          }))
        })
      });

      setSuccess(true);
      return response;
    } catch (err) {
      setError(err.message);
      return null;
    } finally {
      setLoading(false);
    }
  };

  return { verifyUser, submitOrderRequest, loading, error, success };
};